// The page someone was on before they opened the account pages (sign up, log
// in and the rest), so those pages can send them back to it. The last other
// page visited is remembered for the tab, surviving a reload of the sign up page.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CHANGE_PASSWORD_PATH, CONFIRM_PATH, FORGOT_PATH, LOGIN_PATH, PAYMENT_DONE_PATH, PROFILE_PATH, RESET_PATH, SIGNUP_PATH, SUBSCRIBE_PATH } from "./accountRules";
import { LISTING_PATH, SAVED_LISTINGS_PATH } from "./listingRules";
import { PROPERTY_PATH } from "../property/propertyValue";

const KEY = 'tms.returnPage';
const BEFORE_PAYING_KEY = 'tms.beforePaying';
const AFTER_PAYING_KEY = 'tms.afterPaying';
const MAP_PATH = '/map';
const ACCOUNT_PATHS = [SIGNUP_PATH, LOGIN_PATH, CONFIRM_PATH, FORGOT_PATH, RESET_PATH, PROFILE_PATH, CHANGE_PASSWORD_PATH];
// The subscribe pages, skipped when remembering the page to go back to after paying
const PAYING_PATHS = [SUBSCRIBE_PATH, PAYMENT_DONE_PATH];

// A page on this site to go on to: a plain path, never another site. Matches nextParam in AccountUtil.java.
export const isSafeNext = (path) => typeof path === 'string' && /^\/(?!\/)[A-Za-z0-9/_-]{0,200}$/.test(path);

export const isAccountPage = (pathname) => ACCOUNT_PATHS.includes(pathname);

let remembered = null;
let beforePaying = null;
let afterPaying = null;

const store = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (e) { /* storage blocked: keep it in memory */ }
};
const stored = (key) => {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
};

export const rememberPage = ({pathname, search = '', hash = ''}) => {
    if (isAccountPage(pathname)) return;
    remembered = pathname + search + hash;
    store(KEY, remembered);
    if (!PAYING_PATHS.includes(pathname)) {
        beforePaying = remembered;
        store(BEFORE_PAYING_KEY, beforePaying);
    }
};

export const returnPage = () => remembered || stored(KEY);

// The last page before the subscribe pages, to carry on from once a subscription is paid
export const pageBeforePaying = () => beforePaying || stored(BEFORE_PAYING_KEY);

// The page the Subscribe page will send them on to once paid, kept for the tab so a payment
// that leaves the site (PayPal, a bank) and comes back to /payment-done still goes there
export const setPageAfterPaying = (path) => {
    afterPaying = path;
    store(AFTER_PAYING_KEY, path);
};
export const pageAfterPaying = () => afterPaying || stored(AFTER_PAYING_KEY);

const startsWith = (path, prefix) => path === prefix || path.startsWith(prefix + '/') || path.startsWith(prefix + '?');

// The words for a link back to a page, such as "Back to the map"
export const returnLabel = (path) => {
    if (!path) return null;
    if (path === '/' || path.startsWith('/?') || path.startsWith('/#')) {
        return new URLSearchParams(path.slice(path.indexOf('?') + 1)).has('s') ? 'Back to the map' : 'Back to the home page';
    }
    if (startsWith(path, MAP_PATH)) return 'Back to the map';
    if (startsWith(path, '/big-picture')) return 'Back to the Big Picture';
    if (startsWith(path, SAVED_LISTINGS_PATH)) return 'Back to your reports';
    if (startsWith(path, LISTING_PATH)) return 'Back to the listing';
    if (startsWith(path, PROPERTY_PATH)) return 'Back to the property';
    if (startsWith(path, '/subscribe')) return 'Back to subscribe';
    return 'Back to where you were';
};

// Mounted once beside the routes, it remembers each page as it is opened
export const PageTracker = () => {
    const location = useLocation();
    useEffect(() => { rememberPage(location); }, [location]);
    return null;
};
